import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Stack,
  alpha,
  useTheme,
  IconButton,
  Paper,
  Chip,
} from '@mui/material';
import {
  PlayCircle as PlayCircleIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Dashboard as DashboardIcon,
  ViewKanban as ViewKanbanIcon,
  Analytics as AnalyticsIcon,
  EventNote as EventNoteIcon,
  Psychology as PsychologyIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';

interface DemoTab {
  label: string;
  icon: React.ReactElement;
  title: string;
  description: string;
  features: string[];
  imagePlaceholder: string;
  videoUrl?: string;
}

const InteractiveDemo: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);

  const demoTabs: DemoTab[] = [
    {
      label: 'Dashboard',
      icon: <DashboardIcon />,
      title: 'Real-Time Project Dashboard',
      description:
        'Get instant visibility into project health, team velocity, and sprint progress with our intuitive dashboard.',
      features: [
        'Live project metrics',
        'Team velocity tracking',
        'Sprint burndown charts',
        'Risk indicators',
      ],
      imagePlaceholder: 'Dashboard Overview',
      videoUrl: '/landing/videos/dashboard-demo.mp4',
    },
    {
      label: 'Kanban Board',
      icon: <ViewKanbanIcon />,
      title: 'Intelligent Kanban Boards',
      description:
        'Drag-and-drop task management with custom workflows, WIP limits, and real-time collaboration.',
      features: [
        'Drag & drop interface',
        'Custom workflows',
        'WIP limits',
        'Real-time updates',
      ],
      imagePlaceholder: 'Kanban Board View',
    },
    {
      label: 'AI Estimation',
      icon: <PsychologyIcon />,
      title: 'AI-Powered Story Estimation',
      description:
        'Let AI analyze historical data to provide accurate story point estimates in seconds.',
      features: [
        'ML-based predictions',
        'Historical accuracy',
        'Confidence scores',
        'Team calibration',
      ],
      imagePlaceholder: 'AI Estimation Tool',
    },
    {
      label: 'Sprint Planning',
      icon: <EventNoteIcon />,
      title: 'Smart Sprint Planning',
      description:
        'Plan sprints efficiently with capacity planning, velocity tracking, and intelligent backlog prioritization.',
      features: [
        'Capacity planning',
        'Velocity forecasting',
        'Auto-assignment',
        'Dependency mapping',
      ],
      imagePlaceholder: 'Sprint Planning',
    },
    {
      label: 'Analytics',
      icon: <AnalyticsIcon />,
      title: 'Advanced Analytics & Insights',
      description:
        'Deep dive into team performance with comprehensive reports, trends, and predictive insights.',
      features: [
        'Custom reports',
        'Trend analysis',
        'Predictive insights',
        'Export options',
      ],
      imagePlaceholder: 'Analytics Dashboard',
    },
    {
      label: 'Time Tracking',
      icon: <TimerIcon />,
      title: 'Integrated Time Tracking',
      description:
        'Track time spent on tasks with seamless timesheet integration and productivity analytics.',
      features: [
        'Easy time logging',
        'Productivity metrics',
        'Approval workflow',
        'Billing integration',
      ],
      imagePlaceholder: 'Time Tracking',
    },
  ];

  const currentTab = demoTabs[activeTab];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setImageIndex(0);
  };

  const handlePrevImage = () => {
    setImageIndex((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const handleNextImage = () => {
    setImageIndex((prev) => (prev < 2 ? prev + 1 : 2));
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(180deg, 
          ${theme.palette.background.default} 0%, 
          ${alpha(theme.palette.primary.main, 0.02)} 50%,
          ${theme.palette.background.default} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.03,
          backgroundImage: `radial-gradient(circle at 2px 2px, ${theme.palette.primary.main} 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: 6 }}>
          {/* Section Badge */}
          <Chip
            icon={<PlayCircleIcon />}
            label="INTERACTIVE DEMO"
            sx={{
              bgcolor: alpha(theme.palette.secondary.main, 0.1),
              color: theme.palette.secondary.dark,
              fontWeight: 700,
              fontSize: '0.875rem',
              px: 1,
            }}
          />

          {/* Section Title */}
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 800,
              maxWidth: '800px',
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            See TYVRA in Action
          </Typography>

          {/* Section Subtitle */}
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: '700px',
              fontWeight: 400,
              lineHeight: 1.7,
            }}
          >
            Explore key features with interactive demos and see how TYVRA transforms your
            project management workflow
          </Typography>
        </Stack>

        {/* Tabs Navigation */}
        <Box
          sx={{
            mb: 4,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              bgcolor: alpha(theme.palette.background.paper, 0.8),
              borderRadius: 3,
              p: 1,
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
              backdropFilter: 'blur(10px)',
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: 3,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              },
              '& .MuiTab-root': {
                minHeight: 60,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9375rem',
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            {demoTabs.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
                sx={{
                  gap: 1,
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Demo Content */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: theme.palette.background.paper,
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          }}
        >
          <Stack spacing={4}>
            {/* Tab Title and Description */}
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: theme.palette.text.primary,
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                }}
              >
                {currentTab.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.8,
                  fontSize: '1.125rem',
                }}
              >
                {currentTab.description}
              </Typography>
            </Box>

            {/* Features List */}
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1.5}
              sx={{ pb: 2 }}
            >
              {currentTab.features.map((feature, index) => (
                <Chip
                  key={index}
                  label={feature}
                  size="medium"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.dark,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                    },
                  }}
                />
              ))}
            </Stack>

            {/* Demo Image/Video Area */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                minHeight: { xs: '300px', md: '500px' },
                borderRadius: 3,
                overflow: 'hidden',
                bgcolor: alpha(theme.palette.primary.main, 0.03),
                border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              {/* Video Player (if video URL exists) */}
              {currentTab.videoUrl ? (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  {/* Video Placeholder */}
                  <Stack spacing={3} alignItems="center">
                    <PlayCircleIcon
                      sx={{
                        fontSize: 80,
                        color: alpha(theme.palette.primary.main, 0.6),
                      }}
                    />
                    <Typography
                      variant="h5"
                      sx={{
                        color: alpha(theme.palette.text.primary, 0.7),
                        fontWeight: 600,
                      }}
                    >
                      Video Demo Coming Soon
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.text.secondary, 0.6),
                        maxWidth: '400px',
                        textAlign: 'center',
                      }}
                    >
                      Record a 2-3 minute walkthrough of {currentTab.label} and add the video
                      file to /landing/videos/
                    </Typography>
                  </Stack>
                </Box>
              ) : (
                /* Image Placeholder */
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, 
                      ${alpha(theme.palette.primary.light, 0.1)} 0%, 
                      ${alpha(theme.palette.secondary.light, 0.1)} 100%)`,
                    position: 'relative',
                  }}
                >
                  {/* Grid Pattern */}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: `radial-gradient(circle at 2px 2px, ${alpha(
                        theme.palette.primary.main,
                        0.15
                      )} 1px, transparent 0)`,
                      backgroundSize: '32px 32px',
                    }}
                  />

                  <Stack spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ fontSize: 60 }}>{currentTab.icon}</Box>
                    <Typography
                      variant="h4"
                      sx={{
                        color: alpha(theme.palette.primary.main, 0.6),
                        fontWeight: 700,
                      }}
                    >
                      {currentTab.imagePlaceholder}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.text.secondary, 0.5),
                        fontStyle: 'italic',
                        maxWidth: '400px',
                        textAlign: 'center',
                      }}
                    >
                      Add screenshot to /landing/images/screenshots/{currentTab.label.toLowerCase()}.png
                    </Typography>
                  </Stack>
                </Box>
              )}

              {/* Image Navigation Arrows */}
              {!currentTab.videoUrl && (
                <>
                  <IconButton
                    onClick={handlePrevImage}
                    disabled={imageIndex === 0}
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: alpha(theme.palette.background.paper, 0.9),
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        bgcolor: theme.palette.background.paper,
                      },
                      '&:disabled': {
                        opacity: 0.3,
                      },
                    }}
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleNextImage}
                    disabled={imageIndex === 2}
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: alpha(theme.palette.background.paper, 0.9),
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        bgcolor: theme.palette.background.paper,
                      },
                      '&:disabled': {
                        opacity: 0.3,
                      },
                    }}
                  >
                    <NavigateNextIcon />
                  </IconButton>
                </>
              )}
            </Box>

            {/* Image Indicators */}
            {!currentTab.videoUrl && (
              <Stack direction="row" spacing={1} justifyContent="center">
                {[0, 1, 2].map((index) => (
                  <Box
                    key={index}
                    onClick={() => setImageIndex(index)}
                    sx={{
                      width: imageIndex === index ? 40 : 12,
                      height: 12,
                      borderRadius: 6,
                      bgcolor:
                        imageIndex === index
                          ? theme.palette.primary.main
                          : alpha(theme.palette.primary.main, 0.2),
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor:
                          imageIndex === index
                            ? theme.palette.primary.dark
                            : alpha(theme.palette.primary.main, 0.4),
                      },
                    }}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default InteractiveDemo;

